import React, { useState, useEffect } from "react";
import { useFont } from "../../contexts/FontContext/FontContext";
import styles from "./Dropdown.module.css";
import useFilteredOrders from "../../hooks/useFilteredOrders";
import { eliminateRemovedItems } from "../utils/common";

const Dropdown = ( { inputValue, integrationPartnerImage, selectedItems, setSelectedItems, lineItems, setLineItems, orderNumber, setOrderNumber, customerData, setCustomerData, linkType, selectedExchangeOrder, setSelectedExchangeOrder, apiUrl, token, setUniqueOrderId, loadingOrderEditData, setLoadingOrderEditData, validProductIds } ) => {
    const fontSchemes = useFont();    
    const fontFamilyOne = fontSchemes.scheme_one;
    const [matchingOrderNumbers, setMatchingOrderNumbers] = useState([]);
    const [skipRequest, setSkipRequest] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState({}); //for quantity toggle buttons
    const [verticalScrollState, setVerticalScrollState] = useState("vertical_scroll_state_enabled");
    const { orders, loading, error } = useFilteredOrders(inputValue, skipRequest);

    useEffect(() => {
        if (skipRequest) { setSkipRequest(false); }
        if (orders && orders.length > 0) {
            //const filtered = orders.map(order => order.node.name.slice(1)).filter(orderNumber => orderNumber.startsWith(inputValue));
            const filtered = orders.map(order => order.node.name.startsWith('#') ? order.node.name.slice(1) : order.node.name).filter(orderNumber => orderNumber.startsWith(inputValue)); // only slice first character if the first character is a number sign #
            setMatchingOrderNumbers(filtered);
        }
    }, [orders, inputValue]);

    //prevents dropdownbox from being unnecessarily scrollable
    useEffect(() => {
        if (lineItems.length && matchingOrderNumbers.length <= 1) { setVerticalScrollState("vertical_scroll_state_disabled"); }
        else { setVerticalScrollState("vertical_scroll_state_enabled"); }
    }, [lineItems, matchingOrderNumbers])

    // Handler for clicking on an order number
    const handleOrderClick = async (selectedOrderNumber) => {
        try {
            const selectedOrder = orders.find((order) => order.node.name.replace(/^#/, '') === selectedOrderNumber);; // Find the selected order in the orders array. Strip # if there
            setSkipRequest(true); //when selecting an order, the data is already there, so no need to make another request and display loader
            setOrderNumber(selectedOrderNumber);
            setSelectedExchangeOrder(selectedOrderNumber);
            if (selectedOrder) {
                const customer = selectedOrder.node.customer
                const customerObject = {firstName: customer.firstName, lastName: customer.lastName, email: customer.email, address: customer.defaultAddress};
                setUniqueOrderId(selectedOrder.node.id);
                setCustomerData(customerObject);
                // Map each line item to an object with the image URL and variant title
                const lineItems = selectedOrder.node.lineItems.edges.map(edge => ({image: edge.node.product.images.edges[0]?.node.src, productTitle: edge.node.title, productId: edge.node.product.id, variantId: edge.node.variant.id, variantTitle: edge.node.variant.title, productCost: edge.node.variant.price * edge.node.quantity,  productQuantity: edge.node.quantity, maxProductQuantity: edge.node.quantity, productSku: edge.node.variant.sku })); //maxProductQuantity is static
                setLoadingOrderEditData(true);
                const removedItems = await fetchOrderEdits(selectedOrder.node.id.split("/").pop(), apiUrl, token);
                setLoadingOrderEditData(false);
                if (removedItems) {
                    const removedItemsCopy = [...removedItems];
                    const lineItemsCopy = [...lineItems]; // clone to mutate safely
                    var cleanedLineItems = eliminateRemovedItems(removedItemsCopy, lineItemsCopy);
                } else { var cleanedLineItems = lineItems; }// fallback if no refund data
                const filteredCleanedLineItems = cleanedLineItems.filter(item => validProductIds.includes(item.productId)); //remove line items that have product ids that are not valid
                const finalLineItems = filteredCleanedLineItems.length > 0 ? filteredCleanedLineItems : "no items";
                if (linkType === "return") { setLineItems(finalLineItems); }
                else if (linkType === "exchange") { setSelectedItems(finalLineItems); } //customers select items on their end in exchange link view.
            }
        } catch (error) { alert("Error accessing order."); }
    };

    //request refund data to see which items have been removed from orders
    const fetchOrderEdits = async (selectedOrderShopifyId, apiUrl, token) => {
        const response = await fetch(`${apiUrl}/api/get_order_edits/?unique_order_id=${encodeURIComponent(selectedOrderShopifyId)}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        });
        const data = await response.json();
        if (data.data.refunds.length === 0) { return null; }
        const removedItems = data.data.refunds[0].refund_line_items;
        const simplifiedRemovedItems = removedItems.map(item => {
            const rawName = item.line_item.name;
            const cleanedName = rawName.replace(/[\/\-]/g, ' ').replace(/\s+/g, ' ').toLowerCase().trim() // replace slashes and hyphens with a space and collapse multiple spaces into one
            return {name: cleanedName, quantity: item.quantity, sku: item.line_item.sku};
        });
        return simplifiedRemovedItems;
    };

    const toggleItemSelection = (item) => {
        if (selectedItems.includes(item)) { setSelectedItems(selectedItems.filter(i => i !== item)); } // Deselect item   
        else { setSelectedItems([...selectedItems, item]); } // Select item 
    };

    //change quantity of lineItem. min quantity is 1, max is whatever the original product quantity loaded was
    const toggleProductQuantity = (index, increment_decrement) => { 
        setLineItems(prevLineItems => { 
            return prevLineItems.map((item, i) => {
                if (i === index) {
                    const newQuantity = Math.min(item.maxProductQuantity, Math.max(1, item.productQuantity + increment_decrement));
                    if (newQuantity === item.productQuantity) { return item; } // Return original item to prevent re-render
                    const updatedItem = {...item, productQuantity: newQuantity };
                    setSelectedItems(prevSelected => prevSelected.includes(item) ? prevSelected.map(selectedItem => selectedItem === item ? updatedItem : selectedItem) : prevSelected); // Preserve selection when react updates state
                    return updatedItem;
                }
                return item;
            });
        });
    };

    return (
        <div className={`${styles.dropdown_container} ${fontFamilyOne}`} style={{ overflowY: verticalScrollState }}>
            {loading || loadingOrderEditData ? (
                <img className={styles.dropdown_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />
            ) : lineItems.length === 0 ? (
                // Display matching order numbers if no line items are selected
                matchingOrderNumbers.length === 0 ? (
                    <div className={styles.no_orders_found_box}>
                        <p className={styles.no_orders_found}>No orders found.</p>
                    </div>
                ) : (
                    matchingOrderNumbers.map((orderNumber, index) => (
                        <div key={index} className={`${styles.order_number_box} ${index !== matchingOrderNumbers.length - 1 ? "bottom_box_shadow" : ""}`} onClick={() => handleOrderClick(orderNumber)}> 
                            <p className={`${styles.order_number_result_text}`}>Order# {orderNumber}</p>
                            <img src={`${process.env.PUBLIC_URL}/images/${integrationPartnerImage}`} alt="order_number_result_image" className={styles.order_number_result_image} />
                        </div>
                    ))
                )
            ) : (
                lineItems === "no items" ? (
                    <div className={styles.no_orders_found_box}>
                        <p className={styles.no_orders_found}>No items available.</p>
                    </div>
                ) : (
                    // Display line items if an order is selected
                    lineItems.map((item, index) => (
                        <div key={index} className={`${styles.item_box} ${index !== lineItems.length - 1 ? "bottom_box_shadow" : ""}`}>
                            <img src={item.image} alt="order_number_result_image" className={styles.item_image} />
                            <div className={styles.item_text_box}>
                            	<p className={styles.item_text}>{item.itemName}</p>
                            	<p className={styles.item_text}>{item.productTitle} {item.variantTitle === "Default Title" ? "" : ` / ${item.variantTitle}`} {item.maxProductQuantity > 1 && (<span className={`${selectedItems.includes(item) ? styles.quantity_text : ""}`}>({selectedItems.includes(item) ? `Returning ${item.productQuantity}/${item.maxProductQuantity} items` : `${item.maxProductQuantity} items`})</span>)}</p>
                            </div>
                            <img src={`${process.env.PUBLIC_URL}/images/${selectedItems.includes(item) ? "checked_item.png" : "unchecked_item.png"}`} alt="order_number_result_image" className={styles.item_check_mark} onClick={() => toggleItemSelection(item)}/>
                            {item.maxProductQuantity > 1 && item.productQuantity >= 1 && selectedItems.includes(item) && (
                                <div className={`${styles.quantity_toggle_box} fade_in`}>
                                    <img src={hoveredIndex[index]?.up ? `${process.env.PUBLIC_URL}/images/up_arrow_selected.png` : `${process.env.PUBLIC_URL}/images/up_arrow.png`} alt="quantity_up_arrow" className={`${styles.quantity_arrow} ${hoveredIndex[index]?.up ? "fade_in" : ""} ${item.productQuantity === item.maxProductQuantity && styles.dim_arrow_opacity}`} onMouseEnter={() => item.productQuantity !== item.maxProductQuantity && setHoveredIndex(prev => ({ ...prev, [index]: { ...prev[index], up: true } }))} onMouseLeave={() => setHoveredIndex(prev => ({ ...prev, [index]: { ...prev[index], up: false } }))} onClick={() => toggleProductQuantity(index, 1)}/>
                                    <img src={hoveredIndex[index]?.down ? `${process.env.PUBLIC_URL}/images/down_arrow_selected.png` : `${process.env.PUBLIC_URL}/images/down_arrow.png`} alt="quantity_down_arrow" className={`${styles.quantity_arrow} ${hoveredIndex[index]?.down ? "fade_in" : ""} ${item.productQuantity === 1 && styles.dim_arrow_opacity}`} onMouseEnter={() => item.productQuantity !== 1 && setHoveredIndex(prev => ({ ...prev, [index]: { ...prev[index], down: true } }))} onMouseLeave={() => setHoveredIndex(prev => ({ ...prev, [index]: { ...prev[index], down: false } }))} onClick={() => toggleProductQuantity(index, -1)}/>
                                </div>
                            )}         
                        </div>
                    ))
                )
            )}
        </div>
    );
};

export default Dropdown;
